/**
 * «Погодинний прогноз vs факт vs MMS».
 *
 * ВГОРУ — вироблення. Прогноз (голубий) і факт накладені в одній позиції;
 * менше значення попереду, більше позаду, при рівності попереду факт.
 * Стовпчик факту поділено на дві секції, бо заряд УЗЕ береться З ЦЬОГО Ж
 * вироблення (звірено з лічильником: вироблено 968.2 при actual_pv_kw 972.6,
 * з них 299.8 пішло в батарею, у мережу — 624.0):
 *     нижня, зелена     — PV у мережу;
 *     верхня, бурштинова — PV у заряд УЗЕ.
 * Сума секцій дорівнює виробленню, тому висота факту лишається порівнянною
 * з прогнозом, а відсоток відхилення рахується від неї.
 *
 * Розряд УЗЕ (фіолетовий) — теж угору: це віддача в мережу, той самий напрям,
 * що й вироблення, лише з накопичувача, а не з панелей (вечірні години:
 * вироблено 0, передано 394.5, розряд 399.9). Він припадає на години, коли
 * генерація ≈ 0, тож стоїть у власних стовпчиках окремо й не плутається з нею.
 * Окремий стовпчик від нуля, а не стек на факт: факт — про вироблення, а це
 * про віддачу; у ці години його висота сама доходить до лінії MMS. Напрям угору
 * заодно прибирає стрибок осі X і робить заокруглення верхівки тривіальним.
 */
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, LabelList,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { Info, Pencil } from 'lucide-react';
import { useFcmmsTheme } from '../theme';

export interface HourRow {
  hour: number;
  forecastKwh: number | null;
  modelKwh: number | null;
  correctedKwh: number | null;
  isOverridden: boolean;
  editedBy?: string | null;
  factKwh: number | null;
  factToGridKwh?: number | null;
  bessChargeKwh?: number | null;
  bessDischargeKwh?: number | null;
  imbalanceKwh?: number | null;
  socPct?: number | null;
  mmsKwh: number | null;
  mmsIsSeeded?: boolean;
  performancePct: number | null;
  cloudPct: number | null;
  solarWm2: number | null;
  rdnPriceUahMwh?: number | null;
}

interface Props {
  hours: HourRow[];
  dateLabel: string;
  loading: boolean;
  /** Година під курсором. Стан живе на сторінці — таблиця читає той самий. */
  onActiveHourChange?: (hour: number | null) => void;
  /**
   * Заголовок усередині плитки. Коли над сторінкою стоїть ряд кнопок-розділів,
   * секційний хедер НАД карткою зливається з ними в одну смугу — тоді назва
   * має бути в плитці, під якою вона й стоїть.
   */
  titleInside?: boolean;
  /**
   * Підпис графіка. Типово — описова назва «Погодинний прогноз vs факт vs
   * MMS»; у кабінеті ArtemK розділ називається «Торговий графік», і підпис
   * має збігатися з тим, як цю сторінку звуть у роботі.
   */
  title?: string;
}


const BAR = 38;   // ширина стовпчика; barGap = -BAR тримає накладання серій в одній позиції

const n1 = (v: number | null | undefined) =>
  v == null ? '—' : Number(v).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'uk-UA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

export default function ForecastMmsChart({ hours, dateLabel, loading, onActiveHourChange, titleInside, title }: Props) {
  const { t } = useTranslation();
  const T = useFcmmsTheme();
  // Заряд і Розряд УЗЕ — обидва бурштинові (--warn): єдина ідентичність УЗЕ,
  // контраст із зеленим Факт-PV (раніше Розряд був --ok-2 і зливався з Фактом).
  const C_DISCHARGE = T.warn;
  // Решта серій — теж виключно з токенів (§1/§8): реагують на зміну теми.
  const C_FORECAST = T.series3;   // прогноз/план — приглушений брендовий
  const C_FACT = T.ok;            // факт генерації
  const C_CHARGE = T.warn;        // заряд УЗЕ
  const C_MMS = T.series2;        // завантажено на MMS — нейтральний
  const C_SOC = T.ok2;            // SOC УЗЕ — права вісь %
  const C_OVER = T.ok;            // перевиконання
  const C_UNDER = T.warn;         // недовиконання
  const C_PERF = T.warn;          // ручне коригування прогнозу
  const data = useMemo(() => hours.map((h) => {
    const f = h.forecastKwh;
    const a = h.factKwh;
    const both = f != null && a != null;
    const charge = h.bessChargeKwh ?? 0;
    // floor 0 і у фолбеку: відпуск у мережу фізично не буває від'ємним
    const toGrid = h.factToGridKwh ?? (a == null ? null : Math.max(a - charge, 0));
    const behind = both ? a! >= f! : a != null;   // факт позаду, коли він більший
    return {
      ...h,
      hh: String(h.hour).padStart(2, '0'),
      forecastBehind: both ? (f! >= a! ? f : null) : f,
      forecastFront: both && f! < a! ? f : null,
      // Стек факту дублюється на «позаду» і «попереду» — recharts фіксує порядок
      // шарів розміткою, а не по кожній точці, тож потрібні дві пари ключів.
      toGridBehind: behind ? toGrid : null,
      chargeBehind: behind ? (charge || null) : null,
      toGridFront: !behind ? toGrid : null,
      chargeFront: !behind ? (charge || null) : null,
      discharge: h.bessDischargeKwh || null,
      deviationPct: both && f! > 0 ? ((a! - f!) / f!) * 100 : null,
    };
  }), [hours]);

  const hasMms = hours.some((h) => h.mmsKwh != null);
  const seededMms = hours.some((h) => h.mmsIsSeeded);
  const hasCharge = hours.some((h) => (h.bessChargeKwh ?? 0) > 0);
  const hasDischarge = hours.some((h) => (h.bessDischargeKwh ?? 0) > 0);
  const hasSoc = hours.some((h) => h.socPct != null && h.socPct > 0);

  // Стабільна вісь: низ жорстко 0 (жодних від'ємних шкал), верх — максимум
  // доби (прогноз / факт-стек / розряд / MMS), округлений угору до 2 МВт·год,
  // щоб шкала не «підскакувала» між днями й доби були візуально порівнянні.
  const yMax = useMemo(() => {
    const dayMax = Math.max(
      2000,
      ...data.map((d) => Math.max(
        d.forecastKwh ?? 0,
        (typeof d.toGridBehind === 'number' ? d.toGridBehind : (typeof d.toGridFront === 'number' ? d.toGridFront : 0))
          + ((d.chargeBehind ?? d.chargeFront) ?? 0),
        d.discharge ?? 0,
        d.mmsKwh ?? 0,
      )),
    );
    return Math.ceil(dayMax / 2000) * 2000;
  }, [data]);

  const DeviationLabel = (props: any) => {
    const { x, y, width, value, index, series } = props;
    const row = data[index];
    if (!row || row.deviationPct == null) return null;
    // цієї серії в цій годині немає — інакше підпис ліг би на нульову лінію
    if (value == null || value === 0) return null;
    // Серію позначає власний проп series, а не props.dataKey: LabelList не
    // передає dataKey у свій content (recharts виключає його зі списку
    // пропсів). Через це умови нижче раніше не спрацьовували і в годину з
    // зарядом УЗЕ малювалось два однакові підписи — на вершині відпуску й на
    // вершині заряду, за кілька пікселів один від одного.
    //
    // Правило: один підпис на годину, на вершині видимої колонки.
    // Якщо є заряд — вершину стека факту тримає він, а не відпуск.
    if (series === 'toGridBehind' && row.chargeBehind != null) return null;
    // Прогноз підписуємо лише тоді, коли стека факту в цій годині немає:
    // при рівних значеннях «позаду» опиняються обидві колонки одразу.
    if (series === 'forecastBehind' && (row.toGridBehind != null || row.chargeBehind != null)) return null;
    const up = row.deviationPct >= 0;
    return (
      <text x={x + width / 2} y={y - 5} textAnchor="middle"
        fontSize={10} fontWeight={700} fill={up ? C_OVER : C_UNDER}>
        {`${up ? '+' : '−'}${Math.abs(row.deviationPct).toFixed(0)}%`}
      </text>
    );
  };

  const TT = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;
    const r = payload[0]?.payload as (HourRow & { deviationPct: number | null }) | undefined;
    if (!r) return null;
    const line = (name: string, value: string, color: string, bold = false) => (
      <div className="flex items-center justify-between gap-5" style={{ color }}>
        <span>{name}</span><span style={{ fontWeight: bold ? 700 : 600 }}>{value}</span>
      </div>
    );
    const diff = (r.factKwh != null && r.forecastKwh != null) ? r.factKwh - r.forecastKwh : null;
    const charge = r.bessChargeKwh ?? 0;
    const discharge = r.bessDischargeKwh ?? 0;
    return (
      <div style={{ background: T.tooltipBg, border: `1px solid ${T.tooltipBorder}`, borderRadius: 10, padding: '10px 13px', fontSize: 12, color: T.textMain, minWidth: 280 }}>
        <div style={{ fontWeight: 700, marginBottom: 7 }}>
          {dateLabel} · {String(Number(label) - 1).padStart(2, '0')}:00–{label}:00
        </div>
        {line(t('ak_dsp2_generation_forecast'), `${n1(r.forecastKwh)} ${t('ak_dsp2_kwh')}`, C_FORECAST)}
        {line(t('ak_dsp2_actual_generation'), `${n1(r.factKwh)} ${t('ak_dsp2_kwh')}`, C_FACT, true)}
        {charge > 0 && line(t('ak_dsp2_to_grid'), `${n1(r.factToGridKwh)} ${t('ak_dsp2_kwh')}`, C_FACT)}
        {charge > 0 && line(t('ak_dsp2_to_bess_charge'), `${n1(charge)} ${t('ak_dsp2_kwh')}`, C_CHARGE)}
        {r.imbalanceKwh != null && r.imbalanceKwh > 0 &&
          line(t('ak_dsp2_auxiliary_use_imbalance'), `−${n1(r.imbalanceKwh)} ${t('ak_dsp2_kwh')}`, T.textMuted)}
        {discharge > 0 && line(t('ak_dsp2_bess_discharge_to_grid'), `${n1(discharge)} ${t('ak_dsp2_kwh')}`, C_DISCHARGE, true)}
        {r.socPct != null && r.socPct > 0 && line(t('ak_dsp2_bess_soc_hourly_avg'), `${r.socPct} %`, C_SOC)}
        {diff != null && line(
          diff >= 0 ? t('ak_dsp2_overperformance') : t('ak_dsp2_shortfall'),
          `${diff >= 0 ? '+' : '−'}${n1(Math.abs(diff))} ${t('ak_dsp2_kwh')}` +
            (r.deviationPct != null ? ` · ${diff >= 0 ? '+' : '−'}${Math.abs(r.deviationPct).toFixed(1)}%` : ''),
          diff >= 0 ? C_OVER : C_UNDER, true)}
        {line(t('ak_dsp2_uploaded_to_mms'), `${n1(r.mmsKwh)} ${t('ak_dsp2_kwh')}`, C_MMS)}
        <div style={{ borderTop: `1px solid ${T.tooltipBorder}`, margin: '7px 0' }} />
        {line(t('ak_dsp2_cloud_cover'), r.cloudPct == null ? '—' : `${n1(r.cloudPct)} %`, T.textMuted)}
        {line(t('ak_dsp2_solar_radiation'), r.solarWm2 == null ? '—' : `${n1(r.solarWm2)} ${t('ak_dsp2_w_m')}`, T.textMuted)}
        {r.isOverridden && (
          <div style={{ marginTop: 7, color: C_PERF, fontSize: 11, display: 'flex', alignItems: 'center', gap: 5 }}>
            <Pencil size={12} strokeWidth={2} />
            {t('ak_dsp2_forecast_adjusted_manually_model_gave', { m: n1(r.modelKwh) })}
          </div>
        )}
      </div>
    );
  };

  const legendPayload = [
    { value: t('ak_dsp2_forecast_kwh'), type: 'square' as const, color: C_FORECAST },
    { value: t('ak_dsp2_actual_kwh'), type: 'square' as const, color: C_FACT },
    ...(hasCharge ? [{ value: t('ak_dsp2_bess_charge'), type: 'square' as const, color: C_CHARGE }] : []),
    ...(hasDischarge ? [{ value: t('ak_dsp2_bess_discharge'), type: 'square' as const, color: C_DISCHARGE }] : []),
    ...(hasMms ? [{ value: t('ak_dsp2_uploaded_to_mms'), type: 'line' as const, color: C_MMS }] : []),
    ...(hasSoc ? [{ value: t('ak_dsp2_bess_soc'), type: 'line' as const, color: C_SOC }] : []),
  ];

  // Курсор години — тільки вертикаль. Смугу години тут не малюємо: підсвітка
  // колонки лишилась за таблицею, у графіку її роль уже виконують самі
  // стовпчики, і другий прямокутник поверх них лише глушив кольори серій.
  //
  // Координати беремо з points, а не з x/width. Прямокутник із x/y/width/height
  // recharts готує лише для BarChart; у ComposedChart курсор іде гілкою Curve
  // і отримує points — дві точки вертикалі від верху до низу області побудови
  // (node_modules/recharts/es6/component/Cursor.js + util/cursor/getCursorPoints).
  // Саме цю вертикаль малював типовий курсор.
  //
  // Recharts обрізає курсор областю побудови, тож у хедер чи легенду він
  // вийти не може.
  const HourCursor = (props: any) => {
    const { points } = props;
    const a = points?.[0];
    const b = points?.[1];
    if (!a || !b || a.x == null) return null;
    const yTop = Math.min(a.y, b.y);
    const yBottom = Math.max(a.y, b.y);
    return (
      <line x1={a.x} x2={a.x} y1={yTop} y2={yBottom}
        stroke={T.textDim} strokeWidth={1} strokeOpacity={0.7} />
    );
  };

  // Recharts повертає індекс активної категорії; години в data лежать у тому
  // ж порядку, що і в hours, тож індекс переводиться в годину напряму.
  const reportHour = (st: any) => {
    if (!onActiveHourChange) return;
    const i = Number(st?.activeIndex ?? st?.activeTooltipIndex);
    onActiveHourChange(Number.isInteger(i) && hours[i] ? hours[i].hour : null);
  };

  const bs = (color: string) => ({
    fill: color, fillOpacity: 0.38, stroke: color, strokeWidth: 1.4, strokeOpacity: 1,
  });

  // Один і той самий хедер, лише місце різне: над карткою (типово) або
  // всередині неї (коли вище стоїть ряд кнопок-розділів).
  const header = (
    <>
      <div className="font-display text-lg font-semibold" style={{ color: T.textMain }}>{title ?? t('ak_dsp2_hourly_forecast_vs_actual_vs_mms')}</div>
      {!hasMms && (
        <div className="text-[11px] flex items-center gap-1.5" style={{ color: T.textDim }}>
          <Info size={12} strokeWidth={2} />
          {t('ak_dsp2_no_mms_schedule_was_saved_for_this_day')}
        </div>
      )}
    </>
  );

  return (
    <div>
      {!titleInside && (
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2.5 px-1">{header}</div>
      )}

      <div className="tile p-4 sm:p-5">
      {titleInside && (
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">{header}</div>
      )}
      {loading && !hours.length ? (
        <div className="flex items-center justify-center text-xs" style={{ color: T.textMuted, height: 470 }}>{t('ak_dsp2_loading')}</div>
      ) : (
        <>
        <div style={{ height: 470 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 22, right: 14, bottom: 4, left: 8 }}
              barCategoryGap="14%" barGap={-BAR}
              onMouseMove={reportHour}
              onMouseLeave={() => onActiveHourChange?.(null)}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.cardBorder} vertical={false} />
              <XAxis dataKey="hh" tick={{ fontSize: 10, fill: T.textDim }} axisLine={false} tickLine={false} interval={0} />
              <YAxis width={76} domain={[0, yMax]} allowDecimals={false}
                tick={{ fontSize: 10, fill: T.textDim }} axisLine={false} tickLine={false}
                tickFormatter={(v: number) => v.toLocaleString(i18n.language === 'en' ? 'en-GB' : 'uk-UA')}
                label={{ value: t('ak_dsp2_generation_output_kwh'), angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: T.textMuted, textAnchor: 'middle' } }} />
              {hasSoc && (
                <YAxis yAxisId="soc" orientation="right" width={40} domain={[0, 100]}
                  tick={{ fontSize: 10, fill: C_SOC }} axisLine={false} tickLine={false}
                  tickFormatter={(v: number) => `${v}%`} />
              )}
              <Tooltip content={<TT />} cursor={<HourCursor />} />

              {/* задній шар */}
              <Bar dataKey="forecastBehind" radius={[3, 3, 0, 0]} stackId="fc" barSize={BAR} {...bs(C_FORECAST)} legendType="none" isAnimationActive={false}>
                <LabelList dataKey="forecastBehind" content={<DeviationLabel series="forecastBehind" />} />
              </Bar>
              <Bar dataKey="toGridBehind" radius={[3, 3, 0, 0]} stackId="acB" barSize={BAR} {...bs(C_FACT)} legendType="none" isAnimationActive={false}>
                <LabelList dataKey="toGridBehind" content={<DeviationLabel series="toGridBehind" />} />
              </Bar>
              <Bar dataKey="chargeBehind" radius={[3, 3, 0, 0]} stackId="acB" barSize={BAR} {...bs(C_CHARGE)} legendType="none" isAnimationActive={false}>
                <LabelList dataKey="chargeBehind" content={<DeviationLabel series="chargeBehind" />} />
              </Bar>

              {/* передній шар */}
              <Bar dataKey="forecastFront" radius={[3, 3, 0, 0]} stackId="fcF" barSize={BAR} {...bs(C_FORECAST)} legendType="none" isAnimationActive={false} />
              <Bar dataKey="toGridFront" radius={[3, 3, 0, 0]} stackId="acF" barSize={BAR} {...bs(C_FACT)} legendType="none" isAnimationActive={false} />
              <Bar dataKey="chargeFront" radius={[3, 3, 0, 0]} stackId="acF" barSize={BAR} {...bs(C_CHARGE)} legendType="none" isAnimationActive={false} />

              {/* розряд УЗЕ — угору від нуля, як віддача; окремий стек, бо це не вироблення */}
              <Bar dataKey="discharge" radius={[3, 3, 0, 0]} stackId="dis" barSize={BAR} {...bs(C_DISCHARGE)} legendType="none" isAnimationActive={false} />

              {hasMms && (
                <Line type="monotone" dataKey="mmsKwh" name={t('ak_dsp2_uploaded_to_mms')}
                  stroke={C_MMS} strokeWidth={2.4} dot={false} activeDot={{ r: 4 }} connectNulls isAnimationActive={false} />
              )}
              {/* SOC УЗЕ — права вісь %: диспетчер одразу бачить, чому заряд
                  зупинився (батарея повна) або звідки береться вечірній розряд */}
              {hasSoc && (
                <Line yAxisId="soc" type="monotone" dataKey="socPct" name={t('ak_dsp2_bess_soc_2')}
                  stroke={C_SOC} strokeWidth={2} strokeDasharray="5 3"
                  dot={false} activeDot={{ r: 3 }} connectNulls isAnimationActive={false} />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="flex items-center justify-center gap-x-5 gap-y-1.5 flex-wrap pt-3">
            {legendPayload.map((it) => (
              <span key={it.value} className="inline-flex items-center gap-1.5" style={{ fontSize: 12, color: T.textMuted }}>
                {it.type === 'line' ? (
                  <span style={{ width: 16, height: 3, borderRadius: 2, background: it.color, display: 'inline-block' }} />
                ) : (
                  <span style={{ width: 11, height: 11, borderRadius: 3, background: it.color,
                                 opacity: 0.42, border: `1.4px solid ${it.color}`, display: 'inline-block' }} />
                )}
                {it.value}
              </span>
            ))}
        </div>
        </>
      )}
      </div>
    </div>
  );
}
